<template>
  <div class="user-contact" :class="{ 'app-invalid-group': !isComponentValid }">
    <fieldset>
      <legend>
        <span class="app-required-indicator __after">Contact information</span>
      </legend>
      <div class="component-container">
        <ContactPhone :verify="verify" @doPhone="setPhone" @doResetVerify="resetVerify" :embedded="true" />
        <div class="conjunction">and/or</div>
        <ContactEmail :verify="verify" @doEmail="setEmail" @doResetVerify="resetVerify" :embedded="true" />
      </div>
    </fieldset>
    <p hidden ref="noContactInfo" class="app-invalid-msg msg-offset-1">
      Either phone or email is required.
    </p>

    <fieldset>
      <legend>
        <span class="app-required-indicator __after">
          Send me a confirmation and reminder
        </span>
      </legend>
      <div class="app-input-group">
        <div class="app-input-selector">
          <input type="radio" :id="sendYes" :value="sendYes" name="notify"
                 v-model="myNotification" @click="setNotifyMe">
          <label :for="sendYes">Yes <em>(Message and data rates may apply.)</em></label>
        </div>
        <div class="app-input-selector">
          <input type="radio" :id="sendNo" :value="sendNo" name="notify"
                 v-model="myNotification" @click="setNotifyMe">
          <label :for="sendNo">No</label>
        </div>
      </div>
    </fieldset>
    <p hidden ref="noConsentInfo" class="app-invalid-msg msg-offset-2">
      You must choose "Yes" or "No" to indicate your consent to receive reservation confirmation and reminder.
    </p>
  </div>
</template>


<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import ContactPhone from './contact/ContactPhone.vue';
import ContactEmail from './contact/ContactEmail.vue';

// Props, Emits, and Store 
const props = defineProps({
  verify: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits(['doContactValidated', 'doResetVerify']);
const store = useStore();

// Template Refs 
const noContactInfo = ref(null);
const noConsentInfo = ref(null);

// Reactive State 
const myPhone = ref('');
const myEmail = ref('');
const myNotification = ref(undefined); // for selected btn indicator
const isComponentValid = ref(true);    // this parent's validations
const isPhoneValidated = ref(true);   // from ContactPhone
const isEmailValidated = ref(true);   // from ContactEmail
const isModify = ref(false);

const sendYes = 'sendYes';  // for element id and value
const sendNo = 'sendNo';

// Computed props
const reservationSlotId = computed(() => store.state.user.reservationSlotId);
const resDetails = computed(() => store.state.user.resDetails);

// Lifecycle Hooks
onMounted(async () => {
  myNotification.value = getIsConsentProvided();
  if (resDetails.value?.userInfo) {
    const info = resDetails.value.userInfo;
    myPhone.value = info.phone || '';
    myEmail.value = info.email || '';
  }
  isModify.value = await store.dispatch('user/isReservationForUpdate');
});

// Methods 
const getIsConsentProvided = () => {
  if (reservationSlotId.value > 0 && resDetails.value?.userInfo) {
    const userContact = resDetails.value.userInfo.reservationSendConfirmationRequest;
    if (userContact && userContact.isConsentProvided !== undefined) {
      return (userContact.isConsentProvided ? sendYes : sendNo);
    }
  }
  // new reservation should always have undefined
  return undefined;
};

const handleHidden = (data) => {
  const attr = 'hidden';
  const errorElem = data.ref === 'noContactInfo' ? noContactInfo.value : noConsentInfo.value;
  const hasError = data.error;

  if (errorElem) {
    if (hasError) {
      // display error message
      errorElem.removeAttribute(attr);
    } 
    else if (!errorElem.hasAttribute(attr)) {
      // add 'hidden' to <p> if it doesn't exist 
      errorElem.setAttribute(attr, attr);
    }
  }
};

const hasContactInfo = () => (myPhone.value.length > 0 || myEmail.value.length > 0);

const resetVerify = () => {
  // reset children validations
  isPhoneValidated.value = false;
  isEmailValidated.value = false;

  // ask parent to reset its flag
  emit('doResetVerify');
};

const setEmail = (data) => {
  myEmail.value = data.email;
  isEmailValidated.value = data.isValid;
  if (props.verify && isEmailValidated.value && isPhoneValidated.value) {
    continueToNextStep();
  }
};

const setPhone = (data) => {
  myPhone.value = data.phone;
  isPhoneValidated.value = data.isValid;
  if (props.verify && isPhoneValidated.value && isEmailValidated.value) {
    continueToNextStep();
  }
};

const setNotifyMe = (e) => {
  myNotification.value = e.target.value;
};

const toggleRequirementError = () => {
  handleHidden({ ref: 'noContactInfo', error: !hasContactInfo() });
  handleHidden({ ref: 'noConsentInfo', error: !myNotification.value });
};

const continueToNextStep = async () => {
  const hasContact = hasContactInfo();

  // show error message(s)
  toggleRequirementError();

  isComponentValid.value = (hasContact && myNotification.value);
  if (isComponentValid.value) {   // this parent validations
    // all good - pass user info back to parent
    emit('doContactValidated', {
      phone: myPhone.value,
      email: myEmail.value,
      isConsentProvided: (myNotification.value === sendYes),
      isModifyRequest: isModify.value,
    });
  } else {
    // missing contact info and/or consent (highlight the whole box)
    resetVerify();
  }
};

// Expose for Parent Components
defineExpose({
  continueToNextStep,
});
</script>


<style lang="scss" scoped>
.user-contact {
  border: 1px solid var(--ri-gray);
  margin: 0.5em 0 1.5em;
  padding: calc(var(--padding-horizontal) * 0.125) calc(var(--padding-horizontal) * 0.35);
  .component-container {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: flex-start;
    .conjunction {
      // do not use flex alignment due to conditional error <p>
      line-height: 2em;
      padding: 0 0.75em;
      margin-top: 0;
      @media (min-width: 355px) {
        margin-top: 2.5em;
      }
    }
  }
  &.app-invalid-group {     // does not override main's app-invalid-group but as a scrollIntoView indicator
    border: 1px solid var(--ri-red);
    box-shadow: 0 0 2px 2px var(--ri-red);
  }
  input[type="radio"] {
    margin: 0.5em 0;
  }
  .msg-offset {
    &-1 {
      margin-top: -1.25em;
      margin-bottom: 1em;
    }
    &-2 {
      margin-top: -1em;
      margin-bottom: 0.5em;
    }
  }
}
</style>
